import React, { useEffect } from "react";
import CareerNavbar from "./CareerNavbar";
import Footer from "./Footer";
import "../App.css";
import "./Career.css";
import { BiBandAid, BiBullseye, BiPulse, BiBriefcase } from "react-icons/bi";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";


const Career = () => {

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  // Theme color matching Home Page
  const navigate = useNavigate();
  const themeColor = "#f58534"; // Orange
  const queryClient = useQueryClient();

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["jobs-list"],
    queryFn: async () => {
      const res = await axios.get("https://api.nityagroup.tech/api/jobs-list");
      return (res.data || []).filter(
        (v) => v.vertical_id === 15
      );
    },
  });

  const formatDateTime = (dateStr) => {
  if (!dateStr) return "N/A";

  const date = new Date(dateStr.replace(" ", "T"));

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};
  
  return (
    <>
      <CareerNavbar />
      <div className="career-page">
        {/* Hero Section */}
        <div className="career-hero position-relative overflow-hidden">
          <div className="container position-relative z-2">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-5 mb-lg-0" data-aos="fade-right">
                <span className="fw-bold text-uppercase letter-spacing-2 d-block mb-2 career-text-primary">
                  Careers at Jeevandan
                </span>
                <h1 className="display-4 fw-bold mb-4 text-dark career-hero-title-line-height">
                  Join Our Team of{" "}
                  <span className="career-text-primary">Healthcare Heroes</span>
                </h1>
                <p className="lead text-secondary mb-4 career-hero-lead">
                  We are looking for passionate, skilled, and dedicated
                  individuals to help us provide world-class healthcare. Explore
                  our open positions and make a real difference in people's
                  lives.
                </p>
                <div className="d-flex gap-3">
                  <a
                    href="#openings"
                    className="btn btn-lg rounded-pill px-5 py-3 shadow-sm fw-bold text-white"
                    style={{
                      backgroundColor: themeColor,
                      borderColor: themeColor,
                    }}
                  >
                    View Openings
                  </a>
                  <button className="btn btn-outline-dark btn-lg rounded-pill px-5 py-3 fw-bold">
                    Learn More
                  </button>
                </div>
              </div>
              <div className="col-lg-6 text-center" data-aos="fade-left">
                <div className="position-relative d-inline-block">
                  <div className="position-absolute top-0 start-0 w-100 h-100 bg-white rounded-circle opacity-50 blur-lg career-hero-bg-blur"></div>
                  <img
                    src="/assets/img/all-images/team/Doctor - Demo.png"
                    alt="Medical Team"
                    className="img-fluid position-relative z-2 animate-float career-hero-img"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Decorative shapes */}
          <div className="position-absolute top-0 end-0 p-5 mt-5 opacity-10">
            <i className="bi bi-hospital fs-1 display-1 career-text-primary"></i>
          </div>
        </div>

        {/* Why Join Us Section */}
        <section id="journey" className="py-5 bg-white">
          <div className="container py-4">
            <div className="text-center mb-5" data-aos="fade-up">
              <h2 className="fw-bold display-6 mb-3">Why Work With Us?</h2>
              <p className="text-muted mx-auto career-why-work-max-width">
                At Jeevandan Hospital, we prioritize our employees' growth and
                well-being just as much as our patients'.
              </p>
            </div>
            <div className="row g-4 text-center">
              {[
                {
                  icon: "bi-heart-pulse",
                  title: "Health Benefits",
                  desc: "Comprehensive health coverage for you and your family.",
                },
                {
                  icon: "bi-cash-coin",
                  title: "Competitive Pay",
                  desc: "Attractive salary packages and performance bonuses.",
                },
                {
                  icon: "bi-graph-up-arrow",
                  title: "Career Growth",
                  desc: "Continuous learning and career advancement pathways.",
                },
                {
                  icon: "bi-people",
                  title: "Great Culture",
                  desc: "A supportive, collaborative, and inclusive work environment.",
                },
              ].map((item, index) => (
                <div
                  className="col-md-6 col-lg-3"
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="p-4 h-100 rounded-4 border position-relative cursor-pointer career-feature-card">
                    <div className="icon-box d-inline-flex align-items-center justify-content-center rounded-circle mb-4 career-feature-icon-box">
                      <i className={`bi ${item.icon} fs-2`}></i>
                    </div>
                    <h5 className="fw-bold mb-3">{item.title}</h5>
                    <p className="text-muted small mb-0">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Find Dream Job Section (Replaced Openings) */}
        <section id="openings" className="py-5 career-openings-section">
          <div className="container py-4">
            <div className="mb-5">
              <h2 className="fw-bolder display-6 text-dark mb-4">
                Find Your Dream Job
              </h2>

              {/* Search Bar */}
              <div className="bg-white p-2 rounded-4 shadow-sm d-flex flex-column flex-lg-row align-items-center mb-5">
                <div className="d-flex align-items-center px-3 py-3 w-100 w-lg-auto border-bottom border-lg-0 border-end-lg flex-grow-1">
                  <i className="bi bi-pencil me-3 fs-5 career-search-icon"></i>
                  <input
                    type="text"
                    className="form-control border-0 shadow-none"
                    placeholder="Keywords"
                  />
                </div>
                <div className="d-flex align-items-center px-3 py-3 w-100 w-lg-auto border-bottom border-lg-0 border-end-lg flex-grow-1">
                  <i className="bi bi-geo-alt me-3 fs-5 career-search-icon"></i>
                  <select
                    className="form-select border-0 shadow-none text-muted"
                    aria-label="Location"
                  >
                    <option selected>Location</option>
                    <option value="1">Bhopal</option>
                    <option value="2">Indore</option>
                    <option value="3">Remote</option>
                  </select>
                </div>
                <div className="d-flex align-items-center px-3 py-3 w-100 w-lg-auto flex-grow-1">
                  <i className="bi bi-folder2-open me-3 fs-5 career-search-icon"></i>
                  <select
                    className="form-select border-0 shadow-none text-muted"
                    aria-label="Category"
                  >
                    <option selected>Category</option>
                    <option value="1">Medical</option>
                    <option value="2">Admin</option>
                    <option value="3">Support</option>
                  </select>
                </div>
                <div className="p-2 w-100 w-lg-auto">
                  <button className="btn rounded-3 w-100 w-lg-auto d-flex align-items-center justify-content-center career-search-btn">
                    Search
                  </button>
                </div>
              </div>

              {/* Job Cards */}
<div className="row g-4">
  {(Array.isArray(jobs) ? jobs : []).map((job, idx) => {
    // Safe skills convert (string → array)
    const skills = (job?.required_skills || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Fallback icon if API doesn't send icon
    const IconComponent = job?.icon || (() => <i className="bi bi-briefcase" />);
 const isExpired =
      new Date(job?.job_expired_date?.replace(" ", "T")) < new Date();
    return (
      <div
        className="col-lg-4"
        key={idx}
        data-aos="fade-up"
        data-aos-delay={idx * 100}
      >
        <div className="card rounded-4 p-4 shadow-sm h-100 bg-white hover-shadow position-relative border career-job-card">
          <div className="card-body p-0">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center career-job-icon-box"
                  style={{
                    backgroundColor: `${job?.color || "#0d6efd"}15`,
                  }}
                >
                   <img
    src={
      job?.image
        ? `https://api.nityagroup.tech/uploads/${job.image}` // change to your API base URL
        : "/default-job.png"
    }
    alt={job?.job_title || "job"}
    style={{
      width: "70%",
      height: "70%",
      objectFit: "contain",
    }}
  />
                  
                </div>

                <div>
                  <h5 className="fw-bold text-dark mb-1">
                    {job?.job_title || "No Title"}
                  </h5>
                    <span className="text-muted small">
                {job?.experience} Experience
              </span>
                
                </div>
                
              </div>

              <i className="bi bi-bookmark fs-4 cursor-pointer"></i>
            </div>
 {/* DESCRIPTION */}
          <p className="text-muted small mb-3" style={{ minHeight: 40 }}>
            {job?.job_description}
          </p>
            {/* Skills */}
            <div className="mb-4">
              <p className="text-muted small fw-bold mb-2 text-uppercase">
                Skills Required:
              </p>

              <div className="d-flex flex-wrap gap-2">
                {skills.length > 0 ? (
                  skills.map((tag, i) => {
                    const colors = [
                      "text-primary",
                      "text-success",
                      "text-danger",
                      "text-warning",
                      "text-info",
                      "text-secondary",
                    ];

                    return (
                      <span
                        key={i}
                        className={`fw-bold small ${
                          colors[i % colors.length]
                        }`}
                      >
                        {tag}
                        {i < skills.length - 1 ? "," : ""}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-muted small">No skills</span>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="d-flex flex-column gap-3 text-muted fw-medium small mb-4">
              <div className="d-flex align-items-center gap-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", backgroundColor: "#f0f2f5", color: themeColor, }} > <i className="bi bi-briefcase"></i> </div>
                 {formatDateTime(job?.job_expired_date)}
              {isExpired && (
                <span className="text-danger fw-bold ms-2">(Expired)</span>
              )}
              </div>

              <div className="d-flex align-items-center gap-3">
               <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", backgroundColor: "#f0f2f5", color: themeColor, }} > <i className="bi bi-briefcase"></i> </div>
                <span> ₹ {job?.min_salary} — ₹ {job?.max_salary}</span>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", backgroundColor: "#f0f2f5", color: themeColor, }} > <i className="bi bi-briefcase"></i> </div>
                <span>{job?.job_type || "Job Type"}</span>
              </div>
               <div className="d-flex align-items-center gap-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", backgroundColor: "#f0f2f5", color: themeColor, }} >   <i className="bi bi-mortarboard me-2"></i> </div>
                <span> {job?.qualification}</span>
              </div>
            </div>

             {/* APPLY BUTTON */}
          <button
            disabled={isExpired}
            onClick={() => !isExpired && navigate("/career/apply")}
            className="btn w-100 rounded-pill fw-bold"
            style={{
              background: isExpired ? "#191515" : "#f58534",
              color: "#fff",
              cursor: isExpired ? "not-allowed" : "pointer",
            }}
          >
            {isExpired ? "Job Expired" : "Apply Now"}
          </button>
          </div>
        </div>
      </div>
    );
  })}
</div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Career;
